import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StripeTokenResponse } from "components/StripePaymentForm/StripePaymentForm";
import {
  AccountDetails,
  BillingInterval,
  BuyFlowUser,
  EventType,
  JwtLoginSuccessResponse,
  PlanPackageDetails,
  PlanPackageUserFieldDetails,
  PrimaryUser,
  SignupChargeDetails,
  SubscriptionDetails,
  SubscriptionSource,
  TenantUserFieldDetails,
} from "features/types";
import accountServices from "services/account.services";
import fileServices from "services/file.services";
import planPackageServices from "services/planPackage.services";
import { SignupChargeRequest } from "services/types";
import { RootState } from "store";
import userServices from "services/user.services";
import { planPackageIdArray } from "screens/Register/registerUtils";
import { loginSucceed } from "./authSlice";
import { intersection } from "lodash";
import authServices from "services/auth.services";
import { setStoredAuth } from "utils/storageUtils";
import { getPlanType } from "utils/planPackageUtils";
import { convertUserToUserFields } from "utils/userUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";

const findAccountExistingEventTypesAndPlanPackages = (
  subscriptions: SubscriptionDetails[]
) => {
  const accountExistingEventTypes = new Set<EventType>();
  const accountExistingPlanPackageIds = new Set<number>();
  subscriptions.forEach(
    // returns the reservation subscription that the account has (ACTIVE or CANCELLED but not expired), undefined if none found
    (sub) => {
      const subHasValidReservationPlan =
        sub.planPackage.primaryComponent.type === "RESERVATION" &&
        //https://github.com/FanRally/fanrally-core/issues/289
        (sub.status === "ACTIVE" || !sub.expired);
      if (subHasValidReservationPlan) {
        sub.planPackage.eventTypes.forEach((eventType) =>
          accountExistingEventTypes.add(eventType)
        );
        accountExistingPlanPackageIds.add(sub.planPackage.id);
      }
    }
  );
  return { accountExistingEventTypes, accountExistingPlanPackageIds };
};

// Find available plan packages for add membership/plan page
const findAvailablePackages = (
  packages: PlanPackageDetails[],
  accountExistingEventTypes: Set<EventType>,
  accountExistingPlanPackageIds: Set<number>,
  accountHasActiveBankPlan: boolean
) => {
  const result = packages
    .filter((p) => p.available)
    .filter((p) => ["PUBLIC", "URL_ACCOUNT"].includes(p.visibility))
    .filter((p) => {
      const planType = getPlanType(p);
      if (planType === "VOUCHER") {
        return true;
      }
      // Only one active BANK plan is allowed in account
      if (planType === "BANK") {
        return accountHasActiveBankPlan === false;
      }
      if (
        planType === "RESERVATION" &&
        accountExistingPlanPackageIds.has(p.id)
      ) {
        return true; // If account already has the plan package
      }
      // If the current plan package being iterated has event types that do not include any of the event types in accountExistingEventTypes
      // return true, otherwise false
      return (
        intersection(p.eventTypes, [...accountExistingEventTypes]).length === 0
      );
    });
  return result;
};

export const initialChildUser: PrimaryUser = {
  firstName: "",
  lastName: "",
  imageIdentifier: "",
  minor: true,
  email: null,
  phone: "",
  quantity: 1,
  userFields: {},
};

export const initialPrimaryUser: PrimaryUser = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  imageIdentifier: "",
  quantity: 1,
  minor: false,
  userFields: {},
};

export const initialEditingUser: PrimaryUser = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  imageIdentifier: "",
  quantity: 1,
  minor: false,
  userFields: {},
};

interface BuyFlowState {
  planPackages: PlanPackageDetails[];
  planPackageUnavailable: boolean;
  selectedPlanPackage: PlanPackageDetails | null;
  primaryUser: PrimaryUser;
  isEditingPrimaryUser: boolean;
  secondaryUsers: BuyFlowUser[];
  step: number;
  signupCharge: SignupChargeDetails | null;
  selectedBillingInterval: BillingInterval;
  editingUser: BuyFlowUser;
  editingChild: boolean;
  loading: boolean;
  userCreated: boolean;
  allPlanPackages: PlanPackageDetails[];
  shouldCharge: boolean;
}

const initialState: BuyFlowState = {
  planPackages: [],
  allPlanPackages: [],
  planPackageUnavailable: false,
  selectedPlanPackage: null,
  primaryUser: initialPrimaryUser,
  isEditingPrimaryUser: true,
  secondaryUsers: [],
  step: 1,
  shouldCharge: true,
  selectedBillingInterval: "MONTHLY",
  signupCharge: null,
  editingUser: initialEditingUser,
  editingChild: false,
  loading: false,
  userCreated: false,
};

export const uploadUserImages = createAsyncThunk(
  "buyFlow/uploadUserImages",
  async (
    {
      primaryUser,
      secondaryUsers,
    }: { primaryUser: PrimaryUser | undefined; secondaryUsers: BuyFlowUser[] },
    { dispatch }
  ) => {
    // upload primary user profile image
    if (primaryUser !== undefined && primaryUser.imageIdentifier) {
      const response = await fileServices.uploadImage(
        primaryUser.imageIdentifier
      );
      const value = response.value;
      dispatch(setPrimaryUser({ ...primaryUser, imageIdentifier: value }));
    }
    // upload secondary users profile images
    const secondaryUsersWithImageIdentifier: BuyFlowUser[] = [];
    for (let i = 0; i < secondaryUsers.length; i++) {
      const user = secondaryUsers[i];
      let value = "";
      if (user.imageIdentifier) {
        const response = await fileServices.uploadImage(user.imageIdentifier);
        value = response.value;
      }
      secondaryUsersWithImageIdentifier[i] = {
        ...user,
        imageIdentifier: value,
      };
    }
    dispatch(setSecondaryUsers(secondaryUsersWithImageIdentifier));
  }
);
export const createPrimaryUser = createAsyncThunk(
  "buyFlow/createPrimaryUser",
  async (
    {
      tenantUserFields,
      planPackageUserFields,
    }: {
      tenantUserFields: TenantUserFieldDetails[];
      planPackageUserFields: PlanPackageUserFieldDetails[];
    },
    { dispatch, getState }
  ) => {
    try {
      const {
        buyFlow: { primaryUser },
      } = getState() as RootState;
      const response = (await userServices.register(primaryUser, {
        ...convertUserToUserFields({
          user: primaryUser,
          tenantOrPlanPackageUserFields: tenantUserFields,
        }),
        ...convertUserToUserFields({
          user: primaryUser,
          tenantOrPlanPackageUserFields: planPackageUserFields,
        }),
      })) as PrimaryUser;
      dispatch(setPrimaryUser({ ...primaryUser, id: response.id }));
      dispatch(setUserCreated(true));
      return { ...primaryUser, id: response.id };
    } catch (error) {
      throw error;
    }
  }
);

export const createAccount = createAsyncThunk(
  "buyFlow/createAccount",
  async (
    {
      billingToken,
      tenantUserFields,
      planPackageUserFields,
      isRegistering = true,
    }: {
      billingToken: StripeTokenResponse | undefined;
      tenantUserFields: Array<TenantUserFieldDetails>;
      planPackageUserFields: Array<PlanPackageUserFieldDetails>;
      isRegistering?: boolean;
    },
    { dispatch, getState }
  ) => {
    try {
      const {
        buyFlow: {
          primaryUser,
          secondaryUsers,
          selectedPlanPackage,
          selectedBillingInterval,
        },
      } = getState() as RootState;

      // Call login api to get user token, reason why not calling the login action in auth slice
      // is we don't want it to initialize the auth state since it will trigger the useAccount hook to fetch
      // account info, but at this point an account hasn't been created yet.
      let loginResponse = null;
      if (isRegistering) {
        loginResponse = (await authServices.login({
          username: primaryUser.email!,
          password: primaryUser.password!,
        })) as JwtLoginSuccessResponse;
        // Apis will have access to token now
        await setStoredAuth(loginResponse);
      }

      const source: SubscriptionSource = "BUY_FLOW";

      const createAccountParams = {
        primaryUser: {
          userId: primaryUser.id!,
          planPackageIds: planPackageIdArray(
            primaryUser.quantity!,
            selectedPlanPackage!.id
          ),
        },
        members: secondaryUsers.map((user) => ({
          ...user,
          userFields: {
            ...convertUserToUserFields({
              user,
              tenantOrPlanPackageUserFields: tenantUserFields,
            }),
            ...convertUserToUserFields({
              user,
              tenantOrPlanPackageUserFields: planPackageUserFields,
            }),
          },
          planPackageIds: planPackageIdArray(
            user.quantity!,
            selectedPlanPackage!.id
          ),
          type: "SECONDARY",
        })),
        token: billingToken,
        billingInterval: selectedBillingInterval,
        source,
      };
      await accountServices.createAccount(createAccountParams);

      if (isRegistering) {
        // init auth state after account is created
        dispatch(loginSucceed(loginResponse!));
      }
      dispatch(resetBuyFlow(undefined));
    } catch (error) {
      throw error;
    }
  }
);

export const createShopPageAccount = createAsyncThunk(
  "buyflow/createShopPageAccount",
  async (
    {
      billingToken,
      quantity,
      planPackageId,
      tenantUserFields,
      planPackageUserFields,
    }: {
      billingToken: StripeTokenResponse | undefined;
      quantity: number;
      planPackageId: number;
      tenantUserFields: Array<TenantUserFieldDetails>;
      planPackageUserFields: Array<PlanPackageUserFieldDetails>;
    },
    { dispatch, getState }
  ) => {
    try {
      const {
        buyFlow: { primaryUser, selectedBillingInterval, secondaryUsers },
      } = getState() as RootState;

      // Call login api to get user token, reason why not calling the login action in auth slice
      // is we don't want it to initialize the auth state since it will trigger the useAccount hook to fetch
      // account info, but at this point an account hasn't been created yet.
      let loginResponse = (await authServices.login({
        username: primaryUser.email!,
        password: primaryUser.password!,
      })) as JwtLoginSuccessResponse;
      // Apis will have access to token now
      await setStoredAuth(loginResponse);

      const source: SubscriptionSource = "BUY_FLOW";
      const createAccountParams = {
        primaryUser: {
          userId: primaryUser.id!,
          planPackageIds: planPackageIdArray(quantity, planPackageId),
        },
        members: secondaryUsers.map((user) => ({
          ...user,
          userFields: {
            ...convertUserToUserFields({
              user,
              tenantOrPlanPackageUserFields: tenantUserFields,
            }),
            ...convertUserToUserFields({
              user,
              tenantOrPlanPackageUserFields: planPackageUserFields,
            }),
          },
          planPackageIds: planPackageIdArray(user.quantity!, planPackageId),
          type: "SECONDARY",
        })),
        token: billingToken,
        billingInterval: selectedBillingInterval,
        source,
      };
      await accountServices.createAccount(createAccountParams);
      dispatch(loginSucceed(loginResponse!));
      dispatch(resetBuyFlow(undefined));
    } catch (error) {
      throw error;
    }
  }
);
export const fetchSignupCharge = createAsyncThunk(
  "buyFlow/fetchSignupCharge",

  async (params: SignupChargeRequest, { dispatch }) => {
    const response = (await accountServices.signupCharge(
      params
    )) as SignupChargeDetails;
    dispatch(setSignupCharge(response));
  }
);

export const fetchPlanPackages = createAsyncThunk(
  "buyFlow/fetchPlanPackages",

  async (planPackageId: string | null, { dispatch }) => {
    const response = await planPackageServices.find();
    const planPackages = response as PlanPackageDetails[];
    const availablePlanPackages = planPackages
      .filter((plan) => plan.available && !plan.disabled)
      .filter((plan) => {
        if (plan.visibility === "PUBLIC") return true;
        if (planPackageId) {
          return ["URL_ACCOUNT", "URL_ONLY"].includes(plan.visibility);
        }
        return false;
      })
      // If planPackageId is present in url, e.g. /register/123, only show that plan package
      // otherwise show all plan packages
      .filter((plan) => {
        return planPackageId ? plan.id === parseInt(planPackageId) : true;
      });

    if (availablePlanPackages.length === 0) {
      dispatch(setPlanPackageUnavailable(true));
      dispatch(setPlanPackages([]));
      return;
    }
    dispatch(setPlanPackages(availablePlanPackages));
    dispatch(setAllPlanPackages(availablePlanPackages));

    dispatch(setSelectedPlanPackage(availablePlanPackages[0]));
    dispatch(
      setSelectedBillingInterval(availablePlanPackages[0].billingIntervals[0])
    );
  }
);

export const fetchAddMemberPlanPackages = createAsyncThunk(
  "buyflow/fetchAddMemberPlanPackages",
  async (account: AccountDetails, { dispatch }) => {
    const response = await planPackageServices.find();
    const { subscriptions } = account;
    const { accountExistingEventTypes, accountExistingPlanPackageIds } =
      findAccountExistingEventTypesAndPlanPackages(subscriptions);

    let availablePackages = response.filter((p) => p.available);

    dispatch(setAllPlanPackages(availablePackages));

    const accountHasActiveBankPlan = subscriptions.some(
      (sub) =>
        filterIneligibleSubscriptions(sub) &&
        getPlanType(sub.planPackage) === "BANK"
    );

    availablePackages = findAvailablePackages(
      // set up for add membership page
      response,
      accountExistingEventTypes,
      accountExistingPlanPackageIds,
      accountHasActiveBankPlan
    );
    if (availablePackages.length === 0) {
      dispatch(setPlanPackageUnavailable(true));
      return;
    }
    dispatch(setPlanPackages(availablePackages));
    dispatch(setSelectedPlanPackage(availablePackages[0]));
    dispatch(
      setSelectedBillingInterval(availablePackages[0].billingIntervals[0])
    );
  }
);

export const buyFlowSlice = createSlice({
  name: "buyFlow",
  initialState,
  reducers: {
    setPlanPackages: (state, action: PayloadAction<PlanPackageDetails[]>) => {
      state.planPackages = action.payload;
    },
    setAllPlanPackages: (
      state,
      action: PayloadAction<PlanPackageDetails[]>
    ) => {
      state.allPlanPackages = action.payload;
    },
    setPlanPackageUnavailable: (state, action: PayloadAction<boolean>) => {
      state.planPackageUnavailable = action.payload;
    },
    setSelectedPlanPackage: (
      state,
      action: PayloadAction<PlanPackageDetails>
    ) => {
      state.selectedPlanPackage = action.payload;
    },
    setPrimaryUser: (state, action: PayloadAction<PrimaryUser>) => {
      state.primaryUser = action.payload;
    },
    setIsEditingPrimaryUser: (state, action: PayloadAction<boolean>) => {
      state.isEditingPrimaryUser = action.payload;
    },
    setSecondaryUsers: (state, action: PayloadAction<BuyFlowUser[]>) => {
      state.secondaryUsers = action.payload;
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setShouldCharge: (state, action: PayloadAction<boolean>) => {
      state.shouldCharge = action.payload;
    },
    setSignupCharge: (state, action: PayloadAction<SignupChargeDetails>) => {
      state.signupCharge = action.payload;
    },
    setSelectedBillingInterval: (
      state,
      action: PayloadAction<BillingInterval>
    ) => {
      state.selectedBillingInterval = action.payload;
    },
    setEditingChild: (state, action: PayloadAction<boolean>) => {
      state.editingChild = action.payload;
    },
    setEditingUser: (state, action: PayloadAction<BuyFlowUser | null>) => {
      if (action.payload === null) {
        state.editingUser = initialEditingUser;
      } else {
        state.editingUser = action.payload;
      }
    },
    setUserCreated: (state, action: PayloadAction<boolean>) => {
      state.userCreated = action.payload;
    },
    resetBuyFlow: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadUserImages.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadUserImages.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createPrimaryUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPrimaryUser.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createAccount.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createShopPageAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(createShopPageAccount.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createShopPageAccount.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchPlanPackages.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchPlanPackages.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setPrimaryUser,
  setIsEditingPrimaryUser,
  setSecondaryUsers,
  setSelectedPlanPackage,
  setPlanPackages,
  setAllPlanPackages,
  setPlanPackageUnavailable,
  setStep,
  setShouldCharge,
  setSignupCharge,
  setSelectedBillingInterval,
  setEditingUser,
  setUserCreated,
  setEditingChild,
  resetBuyFlow,
} = buyFlowSlice.actions;

export default buyFlowSlice.reducer;
